import React, { Suspense, useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import { LoginPage } from './LoginPage';
import { DashboardLayout } from '../Layout/DashboardLayout';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { PropellerTravelLayout } from '../PropellerTravel/PropellerTravelLayout';
import { AccessDeniedPage } from './AccessDeniedPage';
import {
  checkRouteAccessWithTimeout,
  logUnauthorizedAccess,
  type UnifiedPermission,
} from '../../services/unifiedPermissionService';

// Export AuthorizedRoute for use in specific routes that need custom authorization
export { AuthorizedRoute, withAuthorization } from './AuthorizedRoute';
export { AccessDeniedPage } from './AccessDeniedPage';

interface RouteAuthorizationState {
  checking: boolean;
  authorized: boolean;
  checkedPath: string | null;
  checkedUserId: string | null;
  deniedReason?: string;
}

/**
 * ProtectedRoute with built-in route authorization
 *
 * This component now checks both authentication AND authorization.
 * Route permissions are defined in the route_permissions table.
 */
export const ProtectedRoute = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [authState, setAuthState] = useState<RouteAuthorizationState>({
    checking: true,
    authorized: false,
    checkedPath: null,
    checkedUserId: null,
  });
  // The account that has already been let into a page in this session. While the next page's check runs, the
  // sidebar and header stay mounted for that account (only the page area waits), so the sidebar keeps its open
  // sections, mode and scroll position instead of being rebuilt after every click. The page itself still never
  // renders before its own check allows it.
  const [shellUserId, setShellUserId] = useState<string | null>(null);
  const currentUserId = user?.id ?? null;
  // Sign-out (or no session) drops the kept layout, so the next account starts from the plain check.
  useEffect(() => {
    if (!currentUserId) setShellUserId(null);
  }, [currentUserId]);

  useEffect(() => {
    let cancelled = false;
    const checkedPath = location.pathname;
    const checkedUserId = user?.id ?? null;

    setAuthState({
      checking: true,
      authorized: false,
      checkedPath,
      checkedUserId,
    });

    async function checkAuthorization() {
      if (!checkedUserId) {
        if (!cancelled) {
          setAuthState({
            checking: false,
            authorized: false,
            checkedPath,
            checkedUserId,
          });
        }
        return;
      }

      // Retry logic: getUserContext may return null during initial load
      // because the user_profiles query hasn't resolved yet
      const MAX_RETRIES = 3;
      const RETRY_DELAY_MS = 500;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (cancelled) return;

        try {
          const routeAccess: UnifiedPermission = await checkRouteAccessWithTimeout(
            checkedUserId,
            checkedPath,
          );

          if (routeAccess.hasAccess) {
            // Access granted - set state immediately
            if (!cancelled) {
              setAuthState({
                checking: false,
                authorized: true,
                checkedPath,
                checkedUserId,
              });
              setShellUserId(checkedUserId);
            }
            return;
          }

          // Check if denial is transient (profile not loaded yet)
          const isTransient = routeAccess.deniedReason === 'User not found' ||
            routeAccess.deniedReason === 'Authorization check failed';

          if (isTransient && attempt < MAX_RETRIES) {
            // Wait and retry - profile may still be loading
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
            continue;
          }

          // Final denial - log and show Access Denied
          if (!cancelled) {
            setAuthState({
              checking: false,
              authorized: false,
              checkedPath,
              checkedUserId,
              deniedReason: routeAccess.deniedReason,
            });
            void logUnauthorizedAccess(checkedUserId, {
              route: checkedPath,
              reason: routeAccess.deniedReason,
            }).catch((error) => {
              console.error('Failed to log unauthorized access:', error);
            });
          }
          return;
        } catch (error) {
          console.error('Authorization check failed:', error);
          if (attempt < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
            continue;
          }
          // Every authenticated business route fails closed on authorization
          // transport/runtime failure. No finite route-prefix allowlist can
          // convert an unknown authorization state into access.
          if (!cancelled) {
            setAuthState({
              checking: false,
              authorized: false,
              checkedPath,
              checkedUserId,
              deniedReason: 'Authorization check failed',
            });
          }
          return;
        }
      }
    }

    checkAuthorization();

    return () => { cancelled = true; };
  }, [user?.id, location.pathname]);

  // Show loading during auth check
  if (authLoading) {
    return <LoadingSpinner size="lg" />;
  }

  // Not logged in - show login page
  if (!user) {
    return <LoginPage />;
  }

  const decisionMatchesCurrentRequest = authState.checkedPath === location.pathname
    && authState.checkedUserId === user.id;

  // Effects run after render. Bind the decision to this exact path and user so
  // a prior allow can never render a newly navigated or newly signed-in route.
  if (authState.checking || !decisionMatchesCurrentRequest) {
    if (shellUserId === user.id) {
      return (
        <DashboardLayout>
          <LoadingSpinner size="lg" />
        </DashboardLayout>
      );
    }
    return <LoadingSpinner size="lg" />;
  }

  // Not authorized - show access denied page
  if (!authState.authorized) {
    return <AccessDeniedPage reason={authState.deniedReason} />;
  }

  // Authorized - render the route
  return (
    <DashboardLayout>
      {/* Ctrl/Cmd+K belongs to the header search (MasterSearchPalette in DashboardLayout), which finds
          customers through the Customers page search. The older CrmCommandPalette was also mounted here on the
          same hotkey, so Ctrl+K opened two searches stacked on top of each other (and the CRM one stayed open
          behind the page after the header search closed). It is no longer mounted on every page. */}
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </DashboardLayout>
  );
};

/**
 * PropellerProtectedRoute with authorization
 *
 * Used for the Propeller Travel public platform.
 */
export const PropellerProtectedRoute = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [authState, setAuthState] = useState<RouteAuthorizationState>({
    checking: true,
    authorized: false,
    checkedPath: null,
    checkedUserId: null,
  });

  useEffect(() => {
    let cancelled = false;
    const checkedPath = location.pathname;
    const checkedUserId = user?.id ?? null;

    setAuthState({
      checking: true,
      authorized: false,
      checkedPath,
      checkedUserId,
    });

    async function checkAuthorization() {
      if (!checkedUserId) {
        if (!cancelled) {
          setAuthState({
            checking: false,
            authorized: false,
            checkedPath,
            checkedUserId,
          });
        }
        return;
      }

      const MAX_RETRIES = 3;
      const RETRY_DELAY_MS = 500;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (cancelled) return;

        try {
          const routeAccess = await checkRouteAccessWithTimeout(checkedUserId, checkedPath);

          if (routeAccess.hasAccess) {
            if (!cancelled) {
              setAuthState({
                checking: false,
                authorized: true,
                checkedPath,
                checkedUserId,
              });
            }
            return;
          }

          const isTransient = routeAccess.deniedReason === 'User not found' ||
            routeAccess.deniedReason === 'Authorization check failed';

          if (isTransient && attempt < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
            continue;
          }

          if (!cancelled) {
            setAuthState({
              checking: false,
              authorized: false,
              checkedPath,
              checkedUserId,
              deniedReason: routeAccess.deniedReason,
            });
            void logUnauthorizedAccess(checkedUserId, {
              route: checkedPath,
              reason: routeAccess.deniedReason,
            }).catch((error) => {
              console.error('Failed to log unauthorized access:', error);
            });
          }
          return;
        } catch (error) {
          console.error('Authorization check failed:', error);
          if (attempt < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
            continue;
          }
          // The public-platform authenticated tree also fails closed on an
          // unknown authorization result.
          if (!cancelled) {
            setAuthState({
              checking: false,
              authorized: false,
              checkedPath,
              checkedUserId,
              deniedReason: 'Authorization check failed',
            });
          }
          return;
        }
      }
    }

    checkAuthorization();

    return () => { cancelled = true; };
  }, [user?.id, location.pathname]);

  if (authLoading) {
    return <LoadingSpinner size="lg" />;
  }

  if (!user) {
    return <LoginPage />;
  }

  const decisionMatchesCurrentRequest = authState.checkedPath === location.pathname
    && authState.checkedUserId === user.id;

  if (authState.checking || !decisionMatchesCurrentRequest) {
    return <LoadingSpinner size="lg" />;
  }

  if (!authState.authorized) {
    return <AccessDeniedPage reason={authState.deniedReason} />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner />}>
        <PropellerTravelLayout />
      </Suspense>
    </ErrorBoundary>
  );
};
